import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  CheckCircle2, XCircle, Loader2, ChevronDown, ChevronRight,
  TrendingUp, BarChart3, ShieldCheck, AlertTriangle, Download,
  Upload, FileSpreadsheet, Trash2, Settings2,
} from 'lucide-react';
import clsx from 'clsx';
import { reportsApi, entitiesApi, exportJobsApi } from '@/api/client';
import SearchableSelect from '@/components/SearchableSelect';
import type { Entity } from '@/types';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AccountRow {
  account_code: string;
  account_name: string;
  fs_line_item: string | null;
  parent_code: string | null;
  parent_name: string | null;
  amounts: Record<string, string>;
}

interface ReportData {
  entity_id: number;
  entity_name: string;
  entity_code: string;
  currency: string;
  year: number;
  columns: { key: string; label: string }[];
  available_months?: number[];
  balance_sheet: {
    sections: { assets?: AccountRow[]; liabilities?: AccountRow[]; equity?: AccountRow[] };
    totals: Record<string, Record<string, string>>;
  };
  income_statement: {
    sections: { revenue?: AccountRow[]; expenses?: AccountRow[] };
    totals: Record<string, Record<string, string>>;
  };
}

interface FccsRow {
  account_code: string;
  account_name: string;
  is_section_header: boolean;
  is_total: boolean;
  is_fccs_group: boolean;
  amounts: Record<string, number>;
}

interface FccsData {
  entity_code: string;
  statement_type: string;
  year: number;
  months: string[];
  rows: FccsRow[];
}

type TabType = 'pnl' | 'balance_sheet';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// System API returns keys m1..m12, FCCS returns Jan..Dec
const SYSTEM_KEYS = ['m1', 'm2', 'm3', 'm4', 'm5', 'm6', 'm7', 'm8', 'm9', 'm10', 'm11', 'm12'];
const monthToSystemKey = (month: string) => SYSTEM_KEYS[MONTHS.indexOf(month)] || month;

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtAmt(val: string | number | undefined): string {
  if (val === undefined || val === null) return '\u2014';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num) || num === 0) return '\u2014';
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(Math.round(num)));
  return num < 0 ? `(${formatted})` : formatted;
}

function getYears(): number[] {
  const current = new Date().getFullYear();
  const years: number[] = [];
  for (let y = current + 1; y >= current - 5; y--) years.push(y);
  return years;
}

function isMatch(sysVal: number, fccsVal: number, tolerance = 1): boolean {
  return Math.abs(sysVal - fccsVal) <= tolerance;
}

// ─── Validation Table ───────────────────────────────────────────────────────

function ValidationTable({
  systemRows,
  fccsRows,
  months,
  availableMonths,
}: {
  systemRows: { section: string; rows: AccountRow[] }[];
  fccsRows: FccsRow[];
  months: string[];
  availableMonths?: number[];
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Build FCCS lookup by account_code
  const normalize = (s: string) => s.replace(/\s+/g, "").toLowerCase();
  const fccsMap: Record<string, Record<string, number>> = {};
  fccsRows.forEach((r) => {
    if (r.account_code) {
      fccsMap[r.account_code] = r.amounts;
      const norm = normalize(r.account_code);
      if (!fccsMap[norm]) fccsMap[norm] = r.amounts;
    }
  });

  // Stats
  let totalChecks = 0;
  let matchCount = 0;

  const toggleGroup = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const colWidth = `${Math.max(200, Math.floor(1200 / Math.max(months.length, 1)))}px`;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ tableLayout: 'fixed', minWidth: `${360 + months.length * 220}px` }}>
          <colgroup>
            <col style={{ width: '360px', minWidth: '360px' }} />
            {months.map((m) => (
              <Fragment key={m}>
                <col style={{ width: colWidth, minWidth: '200px' }} />
              </Fragment>
            ))}
          </colgroup>

          {/* Header */}
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-700 text-white">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider sticky left-0 z-30 bg-slate-700" rowSpan={2}>
                Account
              </th>
              {months.map((m) => (
                <th key={m} colSpan={1} className="px-1 py-2 text-center text-xs font-semibold uppercase tracking-wider border-l border-slate-600">
                  {m}
                </th>
              ))}
            </tr>
            <tr className="bg-slate-600 text-white">
              {months.map((m) => (
                <th key={m} className="px-1 py-1.5 text-center border-l border-slate-500">
                  <div className="flex items-center justify-center gap-2 text-[10px]">
                    <span className="text-blue-200">System</span>
                    <span className="text-slate-400">|</span>
                    <span className="text-amber-200">FCCS</span>
                    <span className="text-slate-400">|</span>
                    <span className="text-slate-300">Diff</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {systemRows.map(({ section, rows }) => {
              const sectionTitle = section === 'assets' ? 'Assets'
                : section === 'liabilities' ? 'Liabilities'
                : section === 'equity' ? 'Equity'
                : section === 'revenue' ? 'Revenue'
                : 'Cost of Sales & Expenses';

              // Group by parent_name
              const parentGroups = new Map<string, AccountRow[]>();
              const standalone: AccountRow[] = [];
              rows.forEach((r) => {
                if (r.parent_name) {
                  if (!parentGroups.has(r.parent_name)) parentGroups.set(r.parent_name, []);
                  parentGroups.get(r.parent_name)!.push(r);
                } else {
                  standalone.push(r);
                }
              });

              return (
                <Fragment key={section}>
                  {/* Section header */}
                  <tr className="bg-gradient-to-r from-slate-100 to-slate-50">
                    <td
                      colSpan={1 + months.length}
                      className="px-4 py-2.5 text-xs font-bold text-slate-700 uppercase tracking-widest border-y border-slate-200"
                    >
                      {sectionTitle}
                    </td>
                  </tr>

                  {/* Parent groups */}
                  {[...parentGroups.entries()].map(([parentName, children]) => {
                    const groupKey = `${section}-${parentName}`;
                    const isCollapsed = collapsed[groupKey];

                    return (
                      <Fragment key={parentName}>
                        <tr
                          className="cursor-pointer hover:bg-blue-50/60 bg-gray-50/70 border-b border-gray-100"
                          onClick={() => toggleGroup(groupKey)}
                        >
                          <td className="px-4 py-2 text-xs font-semibold text-gray-700 sticky left-0 z-10 bg-gray-50/70">
                            <span className="inline-flex items-center gap-1">
                              {isCollapsed
                                ? <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                              {parentName}
                            </span>
                          </td>
                          {months.map((m) => (
                            <td key={m} className="px-1 py-2 text-center text-xs text-gray-400 border-l border-gray-100">
                              {isCollapsed ? '' : ''}
                            </td>
                          ))}
                        </tr>

                        {!isCollapsed && children.map((r) => {
                          const fccsAmounts = fccsMap[r.account_code] || fccsMap[normalize(r.account_code)] || {};
                          return (
                            <ValidationRow
                              key={r.account_code}
                              accountCode={r.account_code}
                              accountName={r.account_name}
                              systemAmounts={r.amounts}
                              fccsAmounts={fccsAmounts}
                              months={months}
                              availableMonths={availableMonths}
                              indent
                              onStats={(m, mc) => { totalChecks += m; matchCount += mc; }}
                            />
                          );
                        })}
                      </Fragment>
                    );
                  })}

                  {/* Standalone rows */}
                  {standalone.map((r) => {
                    const fccsAmounts = fccsMap[r.account_code] || fccsMap[normalize(r.account_code)] || {};
                    const isSubtotal = (r as any).is_subtotal || r.account_code?.endsWith('_T');
                    return (
                      <ValidationRow
                        key={r.account_code}
                        accountCode={r.account_code}
                        accountName={r.account_name}
                        systemAmounts={r.amounts}
                        fccsAmounts={fccsAmounts}
                        months={months}
                        availableMonths={availableMonths}
                        indent={false}
                        isSubtotal={isSubtotal}
                        onStats={(m, mc) => { totalChecks += m; matchCount += mc; }}
                      />
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ValidationRow({
  accountCode,
  accountName,
  systemAmounts,
  fccsAmounts,
  months,
  availableMonths,
  indent,
  isSubtotal,
  onStats: _onStats,
}: {
  accountCode: string;
  accountName: string;
  systemAmounts: Record<string, string>;
  fccsAmounts: Record<string, number>;
  months: string[];
  availableMonths?: number[];
  indent: boolean;
  isSubtotal?: boolean;
  onStats: (total: number, matched: number) => void;
}) {
  return (
    <tr className={clsx(
      'transition-colors',
      isSubtotal
        ? 'border-t border-gray-300 bg-gray-50 font-semibold'
        : 'hover:bg-gray-50/80 border-b border-gray-50',
    )}>
      <td className={clsx(
        'pr-3 py-1.5 text-xs sticky left-0 z-10',
        isSubtotal ? 'px-4 font-bold text-gray-800 bg-gray-50'
          : indent ? 'pl-10 text-gray-600 bg-white'
          : 'px-4 font-medium text-gray-700 bg-white',
      )}>
        <span className="text-gray-400 mr-1.5 font-mono text-[11px]">{accountCode}</span>
        {accountName}
      </td>
      {months.map((m, idx) => {
        const monthNum = idx + 1; // Jan=1, Feb=2, ...
        const hasSystemData = !availableMonths || availableMonths.length === 0 || availableMonths.includes(monthNum);
        const sysKey = monthToSystemKey(m);
        const sysVal = parseFloat(systemAmounts[sysKey] || '0');
        // If no system data for this month, treat FCCS as N/A (don't compare)
        const fccsVal = hasSystemData ? (fccsAmounts[m] ?? null) : null;
        const diff = fccsVal !== null ? sysVal - fccsVal : null;
        const matched = fccsVal !== null ? isMatch(sysVal, fccsVal) : null;

        return (
          <td key={m} className="px-1 py-1.5 border-l border-gray-100">
            <div className="flex items-center justify-center gap-1 text-[11px] font-mono tabular-nums">
              {/* System value */}
              <span className="text-gray-700 w-[65px] text-right truncate" title={String(sysVal)}>
                {fmtAmt(sysVal)}
              </span>
              <span className="text-gray-300">|</span>
              {/* FCCS value */}
              <span className="text-amber-700 w-[65px] text-right truncate" title={fccsVal !== null ? String(fccsVal) : 'N/A'}>
                {fccsVal !== null ? fmtAmt(fccsVal) : <span className="text-gray-300">N/A</span>}
              </span>
              <span className="text-gray-300">|</span>
              {/* Diff indicator */}
              <span className="w-[20px] flex justify-center">
                {matched === null ? (
                  <span className="text-gray-300">-</span>
                ) : matched ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <span className="text-red-600 text-[10px] font-semibold" title={`Diff: ${diff?.toFixed(2)}`}>
                    {Math.abs(diff ?? 0) > 999 ? fmtAmt(diff ?? 0) : (diff ?? 0).toFixed(0)}
                  </span>
                )}
              </span>
            </div>
          </td>
        );
      })}
    </tr>
  );
}

// ─── File Upload Card ───────────────────────────────────────────────────────

function FileUploadCard({
  label,
  fileType,
  uploaded,
  onUpload,
  onDelete,
  uploading,
}: {
  label: string;
  fileType: string;
  uploaded: any;
  onUpload: (file: File) => void;
  onDelete: (id: number) => void;
  uploading: boolean;
}) {
  const inputId = `upload-${fileType}`;
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-xs font-semibold text-gray-600 uppercase mb-2">{label}</div>
      {uploaded ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <FileSpreadsheet className="w-4 h-4 text-green-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{uploaded.filename}</p>
              <p className="text-xs text-gray-400">
                {(uploaded.size_bytes / 1024 / 1024).toFixed(1)} MB &middot; {new Date(uploaded.uploaded_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <label htmlFor={inputId}
              className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors" title="Replace file">
              <Upload className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
            </label>
            <button onClick={() => onDelete(uploaded.id)}
              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Delete file">
              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
            </button>
          </div>
        </div>
      ) : (
        <label htmlFor={inputId}
          className="flex flex-col items-center justify-center py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition">
          {uploading ? (
            <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
          ) : (
            <>
              <Upload className="w-6 h-6 text-gray-400 mb-1" />
              <span className="text-xs text-gray-500">Click to upload .xlsx</span>
            </>
          )}
        </label>
      )}
      <input
        id={inputId}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function FinancialValidation() {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState(2025);
  const [activeTab, setActiveTab] = useState<TabType>('pnl');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Export All job state
  const [exportAllStatus, setExportAllStatus] = useState<string>('not_found');
  const [, setExportAllStarted] = useState<string | null>(null);
  const [exportAllFileSize, setExportAllFileSize] = useState<number | null>(null);
  const [exportAllTriggering, setExportAllTriggering] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [fccsData, setFccsData] = useState<FccsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);

  const loadUploadedFiles = useCallback(async () => {
    try {
      const files = await reportsApi.validationFiles();
      setUploadedFiles(files);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    entitiesApi.list({ page: 1, page_size: 500 }).then((data: any) => {
      const list = Array.isArray(data) ? data : (data?.items ?? []);
      setEntities(list);
      if (list.length > 0 && !selectedEntity) {
        setSelectedEntity(String(list[0].id));
      }
    });
    loadUploadedFiles();
  }, []);

  const handleFileUpload = async (fileType: string, file: File) => {
    setUploading(true);
    try {
      await reportsApi.uploadValidationFile(fileType, file);
      await loadUploadedFiles();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileDelete = async (fileId: number) => {
    try {
      await reportsApi.deleteValidationFile(fileId);
      await loadUploadedFiles();
    } catch { /* ignore */ }
  };

  const entityObj = entities.find((e) => String(e.id) === selectedEntity);

  const generateValidation = useCallback(async () => {
    if (!selectedEntity || !entityObj) return;
    setLoading(true);
    setError(null);
    try {
      const stmtType = activeTab === 'pnl' ? 'pnl' : 'balance_sheet';
      const [sysData, fccs] = await Promise.all([
        reportsApi.financialReports({
          entity_id: Number(selectedEntity),
          year: selectedYear,
          scenario: 'Actual',
          view: 'monthly',
        }),
        reportsApi.fccsValidation({
          entity_code: entityObj.code,
          statement_type: stmtType,
          year: selectedYear,
        }),
      ]);
      setReportData(sysData);
      setFccsData(fccs);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.detail || 'Failed to load validation data');
      setReportData(null);
      setFccsData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEntity, selectedYear, activeTab, entityObj]);

  // Rebuild FCCS data when tab changes (if report data already loaded)
  const handleTabChange = async (tab: TabType) => {
    setActiveTab(tab);
    if (!entityObj) return;
    setLoading(true);
    setError(null);
    try {
      const stmtType = tab === 'pnl' ? 'pnl' : 'balance_sheet';
      const fccs = await reportsApi.fccsValidation({
        entity_code: entityObj.code,
        statement_type: stmtType,
        year: selectedYear,
      });
      setFccsData(fccs);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load FCCS data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!entityObj || !reportData) return;
    setExporting(true);
    try {
      const blob = await reportsApi.fccsValidationExport({
        entity_id: Number(selectedEntity),
        entity_code: entityObj.code,
        year: selectedYear,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `validation_${entityObj.code}_${selectedYear}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  // --- Export All Job Handlers ---
  const checkExportAllStatus = useCallback(async () => {
    try {
      const res = await exportJobsApi.status('validation_all', selectedYear);
      setExportAllStatus(res.status);
      setExportAllStarted(res.started_at);
      setExportAllFileSize(res.file_size);
    } catch {
      setExportAllStatus('not_found');
    }
  }, [selectedYear]);

  useEffect(() => {
    checkExportAllStatus();
  }, [checkExportAllStatus]);

  // Poll while running
  useEffect(() => {
    if (exportAllStatus !== 'running') return;
    const interval = setInterval(checkExportAllStatus, 3000);
    return () => clearInterval(interval);
  }, [exportAllStatus, checkExportAllStatus]);

  const handleTriggerExportAll = async () => {
    setExportAllTriggering(true);
    try {
      await exportJobsApi.trigger('validation_all', selectedYear);
      setExportAllStatus('running');
      setExportAllStarted(new Date().toISOString());
    } catch (err: any) {
      console.error('Failed to trigger export:', err);
    } finally {
      setExportAllTriggering(false);
    }
  };

  const handleDownloadExportAll = async () => {
    try {
      await exportJobsApi.download('validation_all', selectedYear);
    } catch (err: any) {
      console.error('Download failed:', err);
    }
  };

  // Build section data for validation table
  const systemSections: { section: string; rows: AccountRow[] }[] = [];
  if (reportData) {
    if (activeTab === 'pnl') {
      if (reportData.income_statement.sections.revenue?.length)
        systemSections.push({ section: 'revenue', rows: reportData.income_statement.sections.revenue });
      if (reportData.income_statement.sections.expenses?.length)
        systemSections.push({ section: 'expenses', rows: reportData.income_statement.sections.expenses });
    } else {
      if (reportData.balance_sheet.sections.assets?.length)
        systemSections.push({ section: 'assets', rows: reportData.balance_sheet.sections.assets });
      if (reportData.balance_sheet.sections.liabilities?.length)
        systemSections.push({ section: 'liabilities', rows: reportData.balance_sheet.sections.liabilities });
      if (reportData.balance_sheet.sections.equity?.length)
        systemSections.push({ section: 'equity', rows: reportData.balance_sheet.sections.equity });
    }
  }

  // Compute match stats
  let totalChecks = 0;
  let matchedChecks = 0;
  let mismatchedChecks = 0;
  const avMonths = reportData?.available_months;
  if (reportData && fccsData) {
    const normalize = (s: string) => s.replace(/\s+/g, "").toLowerCase();
    const fccsMap: Record<string, Record<string, number>> = {};
    fccsData.rows.forEach((r) => {
      if (r.account_code) {
        fccsMap[r.account_code] = r.amounts;
        const norm = normalize(r.account_code);
        if (!fccsMap[norm]) fccsMap[norm] = r.amounts;
      }
    });

    systemSections.forEach(({ rows }) => {
      rows.forEach((r) => {
        const fccsAmounts = fccsMap[r.account_code] || fccsMap[normalize(r.account_code)];
        if (!fccsAmounts) return;
        MONTHS.forEach((m, idx) => {
          // Skip months without system data
          if (avMonths && avMonths.length > 0 && !avMonths.includes(idx + 1)) return;
          const sysKey = monthToSystemKey(m);
          const sysVal = parseFloat(r.amounts[sysKey] || '0');
          const fccsVal = fccsAmounts[m] ?? null;
          if (fccsVal !== null && (sysVal !== 0 || fccsVal !== 0)) {
            totalChecks++;
            if (isMatch(sysVal, fccsVal)) matchedChecks++;
            else mismatchedChecks++;
          }
        });
      });
    });
  }

  const matchPct = totalChecks > 0 ? ((matchedChecks / totalChecks) * 100).toFixed(1) : '0';

  const tabs: { key: TabType; label: string; icon: React.ElementType }[] = [
    { key: 'pnl', label: 'P&L Statement', icon: TrendingUp },
    { key: 'balance_sheet', label: 'Balance Sheet', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary-600" />
            Financial Validation
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {reportData
              ? `${reportData.entity_name} (${reportData.entity_code}) \u00b7 FY ${reportData.year} \u00b7 ${reportData.currency}`
              : 'Compare system-generated statements against FCCS exported data'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export All Entities */}
          {exportAllStatus === 'completed' ? (
            <div className="flex items-center gap-1">
              <button
                onClick={handleDownloadExportAll}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition"
              >
                <Download className="w-4 h-4" />
                Download All ({exportAllFileSize ? `${(exportAllFileSize / 1024 / 1024).toFixed(1)}MB` : ''})
              </button>
              <button
                onClick={handleTriggerExportAll}
                disabled={exportAllTriggering}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                title="Regenerate export with latest data"
              >
                {exportAllTriggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings2 className="w-4 h-4" />}
              </button>
            </div>
          ) : exportAllStatus === 'running' ? (
            <button disabled className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-sm font-medium text-amber-700">
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating All Entities...
            </button>
          ) : (
            <button
              onClick={handleTriggerExportAll}
              disabled={exportAllTriggering}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-primary-300 bg-primary-50 text-sm font-medium text-primary-700 hover:bg-primary-100 disabled:opacity-50 transition"
            >
              {exportAllTriggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Generate All Entity Export
            </button>
          )}

          {/* Single entity export */}
          {reportData && (
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export Excel (PnL + BS)
            </button>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Entity</label>
            <SearchableSelect
              options={entities.map((e) => ({ value: String(e.id), label: e.name, secondary: e.code }))}
              value={selectedEntity}
              onChange={setSelectedEntity}
              placeholder="Select entity..."
            />
          </div>

          <div className="w-28">
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Year</label>
            <select className="w-full rounded-lg border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
              {getYears().map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <button
            onClick={generateValidation}
            disabled={!selectedEntity || loading}
            className="px-6 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold shadow-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Validate
          </button>

          <button
            onClick={() => setShowUpload(!showUpload)}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition',
              showUpload ? 'border-teal-300 bg-teal-50 text-teal-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50',
            )}
          >
            <Settings2 className="w-4 h-4" />
            FCCS Files
          </button>
        </div>

        {/* FCCS File Upload Panel */}
        {showUpload && (
          <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" />
              FCCS Validation Files
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Upload the FCCS-exported Balance Sheet and P&L Excel files. These are stored in the database and used for validation comparison.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {/* PnL Upload */}
              <FileUploadCard
                label="P&L Statement Excel"
                fileType="pnl"
                uploaded={uploadedFiles.find((f) => f.file_type === 'pnl')}
                onUpload={(file) => handleFileUpload('pnl', file)}
                onDelete={(id) => handleFileDelete(id)}
                uploading={uploading}
              />
              {/* BS Upload */}
              <FileUploadCard
                label="Balance Sheet Excel"
                fileType="balance_sheet"
                uploaded={uploadedFiles.find((f) => f.file_type === 'balance_sheet')}
                onUpload={(file) => handleFileUpload('balance_sheet', file)}
                onDelete={(id) => handleFileDelete(id)}
                uploading={uploading}
              />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-6 py-4">
        {reportData && fccsData ? (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-xl border p-4">
                <div className="text-xs text-gray-500 uppercase font-medium">Total Checks</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{totalChecks}</div>
              </div>
              <div className="bg-white rounded-xl border p-4">
                <div className="text-xs text-green-600 uppercase font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Matched
                </div>
                <div className="text-2xl font-bold text-green-600 mt-1">{matchedChecks}</div>
              </div>
              <div className="bg-white rounded-xl border p-4">
                <div className="text-xs text-red-600 uppercase font-medium flex items-center gap-1">
                  <XCircle className="w-3.5 h-3.5" /> Mismatched
                </div>
                <div className="text-2xl font-bold text-red-600 mt-1">{mismatchedChecks}</div>
              </div>
              <div className={clsx(
                'rounded-xl border p-4',
                Number(matchPct) >= 95 ? 'bg-green-50 border-green-200' : Number(matchPct) >= 80 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200',
              )}>
                <div className="text-xs text-gray-500 uppercase font-medium">Match Rate</div>
                <div className={clsx(
                  'text-2xl font-bold mt-1',
                  Number(matchPct) >= 95 ? 'text-green-600' : Number(matchPct) >= 80 ? 'text-amber-600' : 'text-red-600',
                )}>{matchPct}%</div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center justify-between border-b border-gray-200 mb-4">
              <div className="flex gap-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => handleTabChange(tab.key)}
                      className={clsx(
                        'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                        activeTab === tab.key
                          ? 'border-teal-600 text-teal-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> System</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> FCCS</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Match</span>
                <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-500" /> Diff</span>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <ValidationTable
                systemRows={systemSections}
                fccsRows={fccsData.rows}
                months={MONTHS}
                availableMonths={reportData.available_months}
              />
            </div>
          </>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Error</h3>
            <p className="text-sm text-red-500 max-w-md">{error}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <ShieldCheck className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">No Validation Generated</h3>
            <p className="text-sm text-gray-500 max-w-md">
              Select an entity and year, then click Validate to compare system data against FCCS.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
